// Libraries
import React from 'react';
import PropTypes from 'prop-types';
import config from 'config';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { bindActionCreators } from 'redux';
import * as mixpanelTracker from 'app-lib/mixpanel/mixpanelTracker';
import { appLogger } from 'app-lib/helpers/logging';
import { browserHistory} from 'react-router';

// Actions
import * as channelsActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/channel-profile';
import * as FooterActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/footer';
import * as contentsActions from '@getgo/webinar.com-frontend-redux/lib/shared/actions/contents';

// Modules
import { filePickerHelper } from 'app-lib/helpers/filepicker';
import * as userHelper from 'app-lib/helpers/user-helper';
import * as IntercomMessenger from 'app-lib/intercom/intercom';
import { OverlaySpinner } from 'components/SharedComponents/overlaySpinner';
import * as detectBrowser from 'app-lib/helpers/browser-detect-helper';

// Declarations
const logger = appLogger.create('channel-container');

export class ChannelContainer extends React.Component {
// ---------------------------------------- SET UP -----------------------------------------------------------
    constructor(props) {
        super(props);
        this.state = {
            isChannelOwner: this.checkIfChannelOwner(this.props),
            channelKey: this.getChannelKey(this.props)
        };
        this.setChannelKeySuperProperty(this.state.channelKey);
    }

    componentDidMount() {
        detectBrowser.checkNameAndVersion();
        this.initializeFooter();
        filePickerHelper.initialize();
        this.retrieveChannelProfile(this.state.channelKey);
        this.sendGoogleAnalytics(this.state.channelKey);
        this.setIntercomMessenger(this.props.identity);
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.myChannelKey !== nextProps.myChannelKey) {
            this.setState({isChannelOwner: this.checkIfChannelOwner(nextProps)});
        }
        if (this.props.params.user !== nextProps.params.user) {
            this.setState({isChannelOwner: this.checkIfChannelOwner(nextProps),
                channelKey: this.getChannelKey(nextProps)
            });
            this.props.resetChannelState();
            this.props.resetContentsState();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.isChannelOwner !== prevState.isChannelOwner) {
            this.setIntercomMessenger(this.props.identity);
        }
        if (this.state.channelKey !== prevState.channelKey) {
            this.retrieveChannelProfile(this.state.channelKey);
            this.setChannelKeySuperProperty(this.state.channelKey);
        }
    }

    componentWillUnmount() {
        this.props.resetChannelState();
        this.props.resetContentsState();
        this.props.initFooter();
        mixpanelTracker.setIsChannelPublic();
        IntercomMessenger.shutdownIntercomMessenger();
    }
    // ---------------------------------------- CHANNEL PROFILE -----------------------------------------------------------

    retrieveChannelProfile(channelKey) {
        if (!channelKey) {
            this.mountInternalErrorPage('can not retrieve channel profile as channel key does not exist', {});
            return;
        }
        this.props.getChannelProfile(channelKey).catch((error) => {
            this.mountInternalErrorPage(`unable to get the channel profile for channel key ${channelKey}`, error);
        });
    }

    // ---------------------------------------- ORGANIZER CHECKS -----------------------------------------------------------
    setChannelKeySuperProperty(channelKey) {
        const superProperties = {channelKey};
        mixpanelTracker.setSuperProperties(superProperties);
    }

    sendGoogleAnalytics(channelKey) {
        try {
            window.ga('send', {
                hitType: 'event',
                eventCategory: 'User Persona',
                eventAction: 'User Type',
                eventLabel: userHelper.getUserType(this.props.identity)
            });
        } catch (error) {
            logger.warn(`Unable to fire GA event as window.ga does not exist for channel key ${channelKey}`, error);
        }
    }

    getChannelKey(props) {
        return (props.params && props.params.user) ? props.params.user : null;
    }

    checkIfChannelOwner(props) {
        const channelKey = this.getChannelKey(props);
        return !!(props && props.myChannelKey && channelKey && props.myChannelKey === channelKey);
    }

    setIntercomMessenger(identity) {
        if (!this.state.isChannelOwner) {
            return;
        }
        if (identity && identity.me && !identity.isError) {
            const organizer = {
                name: (identity.me.displayName) ? identity.me.displayName : null,
                email: (identity.me.userName) ? identity.me.userName : null
            };
            organizer.email ? IntercomMessenger.initialize(organizer) : logger.error('Unable to load Intercom Widget as there was no organizer email.', identity);

            // update intercom with channelKey
            const channelKey = this.getChannelKey(this.props);
            if (channelKey) {
                IntercomMessenger.updateKey(channelKey, identity.me.userName);
            }
        } else {
            (identity && identity.isError) ? logger.error('User Identity failure', (identity.error) ? identity.error : null) : null;
        }
    }

    // ---------------------------------------- LEARN-MORE PAGE -----------------------------------------------------------
    learnMoreClicked() {
        mixpanelTracker.track(`${mixpanelTracker.learnMoreClicked}`);
    }

    // ---------------------------------------- VIEWS -----------------------------------------------------------
    initializeFooter() {
        const {t} = this.props;
        const styles = {
            backgroundColor: `${this.props.channel && this.props.channel.channelTheme && this.props.channel.channelTheme !== 'default' ? this.props.channel.channelTheme : ''}`,
            color: `${this.props.channel && this.props.channel.channelThemeTextColor && this.props.channel.channelTheme !== 'default' ? this.props.channel.channelThemeTextColor : ''}`
        };
        const rightFooter = [
            <span id="terms&conditions" key="terms&conditions"><a href={`${config.company.termsOfService}`} style={styles} target="_blank" rel="noopener">{t('channel.footer.tnc')}</a></span>,
            <span key="privacy"><a href={`${config.company.privacyPolicy}`} target="_blank" rel="noopener" style={styles} >{t('channel.footer.privacy')}</a></span>,
            <span key="rights" style={styles} >&copy; {new Date().getFullYear()} {t('channel.footer.company')} {t('channel.footer.copyright')}</span>
        ];
        this.props.initFooter({
            isActive: true,
            classes: `generic-page-footer ${this.props.channel && this.props.channel.channelTheme && this.props.channel.channelTheme !== 'default' ? this.props.channel.channelTheme : 'default'}`,
            styles,
            left: <span>{t('channel.footer.powered')}</span>,
            right: rightFooter,
        });
    }

    mountInternalErrorPage(errorMsg, error) {
        logger.error(errorMsg, { error });
        browserHistory.push('/internalError');
    }

    renderSpinner() {
        return <OverlaySpinner />;
    }

    renderChildren() {
        return (
            <div className={`channel ${this.props.channel && this.props.channel.channelTheme && this.props.channel.channelTheme !== 'default' ? '' : 'default'}`}>
                {this.props.children && React.cloneElement(this.props.children, {
                    auth: this.props.auth,
                    identity: this.props.identity,
                    isUserLoggedIn: this.props.isUserLoggedIn,
                    isChannelOwner: this.state.isChannelOwner,
                    channelKey: this.state.channelKey,
                    channel: this.props.channel
                })}
            </div>
        );
    }

    render() {
        if (!this.state.channelKey) {
            this.mountInternalErrorPage('channel key does not exist while rendering', {
                identity: (this.props.identity) ? JSON.stringify(this.props.identity) : null,
                isLoggedIn: (this.props.isUserLoggedIn) ? this.props.isUserLoggedIn : null,
                params: JSON.stringify(this.props.params)
            });

            return null;
        }

        return this.renderChildren();
    }
}

ChannelContainer.propTypes = {
    children: PropTypes.element,
    params: PropTypes.object.isRequired,
    auth: PropTypes.object,
    identity: PropTypes.object,
    isUserLoggedIn: PropTypes.bool,
    myChannelKey: PropTypes.string,
    getChannelProfile: PropTypes.func,
    channel: PropTypes.object,
    initFooter: PropTypes.func,
    t: PropTypes.func,
    channelProfileFetching: PropTypes.number,
    resetContentsState: PropTypes.func,
    resetChannelState: PropTypes.func
};

export default connect(
    state => ({
        channel: state.channel.channel,
        channelProfileFetching: state.channel.isFetching,
    }),
    dispatch => bindActionCreators(Object.assign({}, channelsActions, FooterActions, contentsActions), dispatch)
)(translate(['translation'])(ChannelContainer));



// WEBPACK FOOTER //
// ./src/components/channel/channel-container.js